import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import { uploadDicomFile } from '../../utils/uploadDicomFile';

const DicomFileUpload = ({ onUploadSuccess, disabled }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const validExtensions = ['dcm', 'dicom', 'zip'];
    
    if (!validExtensions.includes(fileExtension)) {
      toast.error('Please select a DICOM (.dcm/.dicom) or ZIP file');
      return;
    }
    
    // Check if file is too large (show warning but still allow upload)
    if (file.size > 100 * 1024 * 1024) { // 100MB
      toast.warning('File is larger than 100MB. Upload may take some time.');
    }
    
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || disabled) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      // Show a message for large files
      if (selectedFile.size > 50 * 1024 * 1024) { // 50MB
        toast.info('Large file detected. Upload may take several minutes. Please be patient.');
      }
      
      // Track real upload progress
      const handleProgress = (progress) => {
        // Cap at 95% - reserve the last 5% for processing
        setUploadProgress(Math.min(progress, 95));
      };
      
      // Upload the file with progress tracking
      const result = await uploadDicomFile(selectedFile, handleProgress);
      
      // Set to 100% when complete
      setUploadProgress(100);
      
      // The result is already a JavaScript object, no need to parse
      const parsedData = result;
      
      // Notify success
      toast.success('DICOM file uploaded and processed successfully');
      
      // Pass the data to the parent component
      onUploadSuccess(parsedData);
      
      // Reset the file input
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      // Clear any existing toasts
      toast.dismiss();
      
      // Handle specific error types
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast.error('Upload timed out. The file may be too large or the server is taking too long to respond.');
      } else if (error.message.includes('Network Error')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error(`Upload failed: ${error.message || 'Unknown error'}`);
      }
      
      console.error('DICOM upload error:', error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="mb-6 p-4 border rounded-md bg-gray-50">
      <h3 className="text-lg font-medium mb-2">Upload DICOM or ZIP File</h3>
      <p className="text-sm text-gray-600 mb-4">
        Upload a DICOM or ZIP file to automatically fill the form with patient data.
      </p>
      
      <div className="flex flex-col sm:flex-row items-start gap-3">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          disabled={isUploading || disabled}
          className="flex-1 text-sm"
          accept=".dcm,.dicom,.zip"
        />
        
        <div className="flex gap-2">
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading || disabled}
            className="whitespace-nowrap"
          >
            {isUploading ? 'Uploading...' : 'Upload & Process'}
          </Button>
          
          {selectedFile && !isUploading && (
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={disabled}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
      
      {selectedFile && !isUploading && (
        <div className="mt-2 text-sm text-gray-700">
          Selected file: {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
        </div>
      )}
      
      {isUploading && (
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-1">
            <Spinner size="sm" />
            <span className="text-sm font-medium">
              {uploadProgress < 95 
                ? 'Uploading file...' 
                : 'Processing DICOM data...'}
            </span>
            <span className="text-sm text-gray-500">
              {Math.round(uploadProgress)}%
            </span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-primary-600 h-2.5 rounded-full transition-all duration-300" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          
          <p className="text-xs text-gray-500 mt-1">
            Large files may take several minutes to upload and process.
            {uploadProgress > 0 && uploadProgress < 100 && uploadProgress === Math.round(uploadProgress) && 
              <span className="ml-1 font-medium">
                Please don't refresh the page.
              </span>
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default DicomFileUpload;
